import React, { FC, useEffect, useRef, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { colors } from '../../../../theme/palette'
import { L, L_HTML } from '../../../providers/Lokalise'
import { Button } from '../../../ui/Button/Button'
import { Typography } from '../../../ui/Typography/Typography'
import { CaseDetail } from '../../../../models/CaseDetail'
import { CaseNote } from '../../../../models/CaseNote'
import { formatDate } from '../../../../utils/format'
import { deleteCaseNote } from '../../../../api/salary'
import { useErrorDialog } from '../../../../hooks/useErrorDialog'
import { ErrorDisplay } from '../Contact/ContactDialog'

interface CaseNoteCardProps {
    note: CaseNote
    currentCase: CaseDetail
    onUpdated?: () => void
}

const useStyles = createUseStyles({
    card: {
        position: 'relative',
        backgroundColor: colors.white,
        width: '100%',
        color: 'black',
        marginBottom: '10px',
        borderRadius: '4px',
        border: `1px solid ${colors.borderColor}`,
        padding: '1rem',
        transition: 'all 0.5s linear',
    },
    actions: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        display: 'flex',
        gap: '10px',
    },
    date: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '20px',
    },
    deleteButtons: {
        display: 'flex',
        transition: 'all .3s ease-in',
        marginTop: '20px',
    },
    title: {
        display: '-webkit-box',
        WebkitLineClamp: '1',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    titleOpen: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    content: {
        marginTop: '10px',
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        whiteSpace: 'pre-wrap',
        textOverflow: 'ellipsis',
    },
    contentOpen: {
        marginTop: '10px',
        display: '-webkit-box',
        whiteSpace: 'pre-wrap',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
})

const CaseNoteCard: FC<CaseNoteCardProps> = ({
    note,
    currentCase,
    onUpdated,
}) => {
    const classes = useStyles()
    const [showDelete, setShowDelete] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [readMore, setReadMore] = useState(false)
    const [isMore, setIsMore] = useState(false)
    const contentRef = useRef<HTMLSpanElement>()
    const titleRef = useRef<HTMLSpanElement>()
    const { showError, ErrorDialogComponent } = useErrorDialog() as any

    const checkLines = (ref, lines: number): void => {
        if (!ref.current) return

        const lineHeight = window
            .getComputedStyle(ref.current)
            .getPropertyValue('line-height')

        const lineCount = Math.round(
            ref.current.scrollHeight / parseFloat(lineHeight)
        )
        if (lineCount > lines) {
            setIsMore(true)
        }
    }

    const onHandleDelete = async (): Promise<void> => {
        setIsLoading(true)

        const response = await deleteCaseNote({
            caseId: currentCase.caseId,
            caseRegTime: currentCase.caseRegistrationTime,
            noteId: note.id,
        })

        if (response.error) {
            showError(<ErrorDisplay errors={response.error.errors} />)
            setIsLoading(false)
            return
        }
        if (response) {
            onUpdated?.()
        }

        setShowDelete(false)
        setIsLoading(false)
    }

    useEffect(() => {
        checkLines(contentRef, 2)
    }, [contentRef, note])

    useEffect(() => {
        checkLines(titleRef, 1)
    }, [titleRef, note])

    return (
        <div className={classes.card}>
            {/* ------- text content --------- */}
            <Typography size="XS">
                <span
                    ref={contentRef}
                    className={readMore ? classes.contentOpen : classes.content}
                    dangerouslySetInnerHTML={{ __html: note.message }}
                />
            </Typography>
            {/* ---------- date -------- */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'end',
                    marginTop: '.6rem',
                }}
            >
                {isMore && (
                    <Button
                        type="action"
                        style={{
                            textDecoration: 'underline',
                        }}
                        onClick={() => setReadMore((prev) => !prev)}
                    >
                        {readMore
                            ? L_HTML('LØN_NOTER_SIDEBAR_VIS_MINDRE')
                            : L_HTML('LØN_NOTER_SIDEBAR_VIS_MERE')}
                    </Button>
                )}
            </div>
            <Typography
                size="S"
                weight="bold"
                color={colors.gray500}
                style={{
                    marginTop: '1.5rem',
                    paddingBottom: '.5rem',
                    display: 'flex',
                    gap: '10px',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    size="XS"
                    color={colors.gray900}
                    style={{ flexGrow: 1 }}
                >
                    {formatDate(new Date(note.createdAt))}
                </Typography>

                <Typography size="XS" color={colors.gray900}>
                    {L('LØN_SAGS_NOTE_SIDEBAR_TILFØJET_AF')}: {note.createdBy}
                </Typography>
            </Typography>
            {/* {note.isDeletable && (
                <div className={classes.actions}>
                    <Button type="action" onClick={() => setShowDelete(true)}>
                        <CircleIcon2
                            width="34px"
                            backgroundColor={colors.red100}
                        >
                            <DeleteFill width="20px" color={colors.red600} />
                        </CircleIcon2>
                    </Button>
                </div>
            )} */}
            {showDelete && note.isDeletable ? (
                <div className={`${classes.deleteButtons} page-up`}>
                    <Button
                        onClick={() => setShowDelete(false)}
                        size="xlarge"
                        type="secondary"
                        style={{ flexGrow: 1 }}
                    >
                        {L('NOTER_SIDEBAR_ANNULLER_KNAP')}
                    </Button>
                    <Button
                        onClick={onHandleDelete}
                        type="danger"
                        size="xlarge"
                        style={{ flexGrow: 1 }}
                        loading={isLoading}
                    >
                        Slet
                        {L('LØN_SAGS_NOTE_KORT_SLET_KNAP')}
                    </Button>
                </div>
            ) : null}
            <ErrorDialogComponent />
        </div>
    )
}

export { CaseNoteCard }
